import type { ComponentType } from "react";
import {
    GraduationCap,
    Stethoscope,
    UserRound,
    Users,
} from "lucide-react";
import { HealthLang } from "../healthTypes";
import { DoctorDashboardMiniPreview } from "./DoctorDashboardMiniPreview";
import { NurseBedsideTabletPreview } from "./NurseBedsideTabletPreview";
import { PatientIntakeMobilePreview } from "./PatientIntakeMobilePreview";

const snippetShell =
    "relative w-full overflow-hidden rounded-lg border border-grey-100/90 bg-white text-left shadow-[0_6px_18px_rgba(16,24,40,0.08)] [text-align:left]";
const snippetInner = "px-3.5 pb-3 pt-3 text-left [text-align:left]";

const LABELS = {
    suiteHeader: {
        En: "Aimma products",
        Zh: "Aimma 產品",
        Jp: "Aimma製品",
    },
    doctorProduct: {
        En: "Aimma for Doctor",
        Zh: "Aimma 醫師版",
        Jp: "Aimma 医師向け",
    },
    nurseProduct: {
        En: "Aimma for Nurse",
        Zh: "Aimma 護理版",
        Jp: "Aimma 看護向け",
    },
    patientProduct: {
        En: "Aimma for Patient",
        Zh: "Aimma 病人版",
        Jp: "Aimma 患者向け",
    },
    educationProduct: {
        En: "Education",
        Zh: "教育",
        Jp: "教育",
    },
    clinicalWorkspace: {
        En: "Clinical workspace",
        Zh: "臨床工作區",
        Jp: "臨床ワークスペース",
    },
    nursingWorkspace: {
        En: "Nursing workspace",
        Zh: "護理工作區",
        Jp: "看護ワークスペース",
    },
    intakeComingSoon: {
        En: "Intake · coming soon",
        Zh: "問診 · 即將推出",
        Jp: "インテーク · 近日公開",
    },
    learningResources: {
        En: "Learning & resources",
        Zh: "學習與資源",
        Jp: "学習とリソース",
    },
    suitePreviewAria: {
        En: "Aimma product suite interface preview",
        Zh: "Aimma 產品組合介面預覽",
        Jp: "Aimma製品スイート画面",
    },
} as const;

function t(key: keyof typeof LABELS, lang: HealthLang): string {
    return LABELS[key][lang] ?? LABELS[key].En;
}

interface PreviewProps {
    lang: HealthLang;
}

export function DoctorHandoffCardPreview({ lang }: PreviewProps) {
    return <DoctorDashboardMiniPreview lang={lang} />;
}

export function NurseHandoffCardPreview({ lang }: PreviewProps) {
    return <NurseBedsideTabletPreview lang={lang} />;
}

export function PatientIntakeCardPreview({ lang }: PreviewProps) {
    return <PatientIntakeMobilePreview lang={lang} />;
}

const SUITE_ROWS = [
    {
        icon: Stethoscope,
        iconClass: "text-teal-600 bg-teal-50",
        titleKey: "doctorProduct" as const,
        subtitleKey: "clinicalWorkspace" as const,
    },
    {
        icon: Users,
        iconClass: "text-amber-600 bg-[rgba(217,119,6,0.1)]",
        titleKey: "nurseProduct" as const,
        subtitleKey: "nursingWorkspace" as const,
    },
    {
        icon: UserRound,
        iconClass: "text-sky-500 bg-sky-50/80",
        titleKey: "patientProduct" as const,
        subtitleKey: "intakeComingSoon" as const,
        muted: true,
    },
    {
        icon: GraduationCap,
        iconClass: "text-violet-600 bg-violet-50",
        titleKey: "educationProduct" as const,
        subtitleKey: "learningResources" as const,
    },
] as const;

const EDUCATION_LOOP_LABELS = {
    header: {
        En: "Learning loop",
        Zh: "學習循環",
        Jp: "学習ループ",
    },
    steps: {
        En: "Prepare · Practice · Learn · Defend · Reflect",
        Zh: "準備 · 練習 · 學習 · 答辯 · 反思",
        Jp: "準備 · 練習 · 学習 · ディフェンス · 振り返り",
    },
    previewAria: {
        En: "Medical Sensei learning loop preview",
        Zh: "Medical Sensei 學習循環預覽",
        Jp: "Medical Sensei 学習ループプレビュー",
    },
} as const;

const chatThreadClass =
    "flex flex-col items-stretch gap-1.5 text-left [text-align:left]";
const chatBubbleLeftClass =
    "self-start max-w-[88%] rounded-lg rounded-tl-sm px-2 py-1.5 text-left [text-align:left]";
const chatBubbleRightClass =
    "self-end max-w-[88%] rounded-lg rounded-tr-sm px-2 py-1.5 text-left [text-align:left]";
const chatLabelClass = "text-left text-[8px] font-semibold [text-align:left]";
const chatBodyClass =
    "text-left text-[9px] leading-snug text-grey-700 [text-align:left]";

export function BusinessSenseiCardPreview({ lang }: PreviewProps) {
    const labels = {
        aria: {
            En: "Business Sensei virtual client practice preview",
            Zh: "商學 Sensei 虛擬客戶練習預覽",
            Jp: "商学 Sensei バーチャル顧客練習プレビュー",
        },
        header: {
            En: "Virtual client · Briefing",
            Zh: "虛擬客戶 · 簡報情境",
            Jp: "バーチャル顧客 · ブリーフィング",
        },
        clientLabel: {
            En: "Client",
            Zh: "虛擬客戶",
            Jp: "顧客",
        },
        youLabel: {
            En: "You",
            Zh: "你",
            Jp: "あなた",
        },
        ai1: {
            En: "If budget drops 20%, how would you adjust scope?",
            Zh: "若預算縮減 20%，你會如何調整方案？",
            Jp: "予算が20%減った場合、提案をどう調整しますか？",
        },
        user1: {
            En: "We can phase delivery and protect core outcomes.",
            Zh: "可分階段交付，先保住核心成果。",
            Jp: "段階的に納品し、核心成果を守れます。",
        },
        ai2: {
            En: "What timeline would you commit to?",
            Zh: "那你願意承諾的時程是？",
            Jp: "では、どのスケジュールを約束できますか？",
        },
        negotiation: { En: "Negotiation", Zh: "談判", Jp: "交渉" },
        leadership: { En: "Leadership", Zh: "領導", Jp: "リーダーシップ" },
        feedback: {
            En: "Feedback: clear structure — add one trade-off.",
            Zh: "回饋：論點清楚，可再補一個取捨說明。",
            Jp: "フィードバック：構成は明確。トレードオフを追加。",
        },
    } as const;

    const t = (key: keyof typeof labels) =>
        labels[key][lang] ?? labels[key].En;

    return (
        <div
            className={`${snippetShell} min-w-[12.75rem]`}
            role="img"
            aria-label={t("aria")}
        >
            <div className={`${snippetInner} space-y-2.5`}>
                <div className="flex items-center justify-between gap-2 border-b border-grey-100/90 pb-2">
                    <p className="text-left text-[10px] font-semibold text-grey-800">
                        {t("header")}
                    </p>
                    <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[8px] font-semibold text-amber-700">
                        Live
                    </span>
                </div>

                <div className={chatThreadClass}>
                    <div
                        className={`${chatBubbleLeftClass} bg-grey-50`}
                    >
                        <p className={`${chatLabelClass} text-grey-500`}>
                            {t("clientLabel")}
                        </p>
                        <p className={chatBodyClass}>{t("ai1")}</p>
                    </div>
                    <div
                        className={`${chatBubbleRightClass} bg-amber-50`}
                    >
                        <p className={`${chatLabelClass} text-amber-700`}>
                            {t("youLabel")}
                        </p>
                        <p className={chatBodyClass}>{t("user1")}</p>
                    </div>
                    <div
                        className={`${chatBubbleLeftClass} bg-grey-50`}
                    >
                        <p className={`${chatLabelClass} text-grey-500`}>
                            {t("clientLabel")}
                        </p>
                        <p className={chatBodyClass}>{t("ai2")}</p>
                    </div>
                </div>

                <div className="space-y-1 border-t border-grey-100/90 pt-2">
                    <div className="flex items-center gap-2">
                        <span className="w-10 shrink-0 text-[8px] text-grey-500">
                            {t("negotiation")}
                        </span>
                        <div className="flex flex-1 gap-0.5">
                            <span className="h-1 flex-1 rounded-full bg-amber-500" />
                            <span className="h-1 flex-1 rounded-full bg-amber-500" />
                            <span className="h-1 flex-1 rounded-full bg-grey-200" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-10 shrink-0 text-[8px] text-grey-500">
                            {t("leadership")}
                        </span>
                        <div className="flex flex-1 gap-0.5">
                            <span className="h-1 flex-1 rounded-full bg-amber-400" />
                            <span className="h-1 flex-1 rounded-full bg-amber-400" />
                            <span className="h-1 flex-1 rounded-full bg-amber-400" />
                        </div>
                    </div>
                    <p className="text-left text-[8px] leading-snug text-grey-500">
                        {t("feedback")}
                    </p>
                </div>
            </div>
        </div>
    );
}

export function K12SenseiCardPreview({ lang }: PreviewProps) {
    const labels = {
        aria: {
            En: "K-12 Learning Sensei study session preview",
            Zh: "國中小全科學習 Sensei 練習預覽",
            Jp: "国中小全科学習 Sensei 練習プレビュー",
        },
        header: {
            En: "All-subjects · Math",
            Zh: "全科練習 · 數學",
            Jp: "全科練習 · 数学",
        },
        tabs: {
            En: ["Math", "Chinese", "Science"],
            Zh: ["數學", "國文", "自然"],
            Jp: ["数学", "国語", "理科"],
        },
        question: {
            En: "Xiao Hua has 12 candies to share equally among 3 friends. How many each?",
            Zh: "小華有 12 顆糖，要平均分給 3 位同學，每人幾顆？",
            Jp: "シャオファは12個の飴を3人で分けます。一人何個？",
        },
        student: {
            En: "Should I divide by 3 first?",
            Zh: "要先除以 3 嗎？",
            Jp: "まず3で割りますか？",
        },
        senseiOpener: {
            En: "Read again — what needs to be shared equally?",
            Zh: "再看題目——什麼要「平均分」？",
            Jp: "もう一度読みましょう——何を均等に分けますか？",
        },
        sensei: {
            En: "Think about the total first — what does “equal shares” mean?",
            Zh: "先想想總數——「平均分」代表什麼？",
            Jp: "まず合計を考えましょう。「均等に分ける」とは？",
        },
        step: {
            En: "Step 2 of 4",
            Zh: "步驟 2 / 4",
            Jp: "ステップ 2 / 4",
        },
        review: {
            En: "Mistake review",
            Zh: "錯題複習",
            Jp: "復習",
        },
    } as const;

    const tabs = labels.tabs[lang] ?? labels.tabs.En;

    return (
        <div
            className={`${snippetShell} min-w-[12.75rem]`}
            role="img"
            aria-label={labels.aria[lang] ?? labels.aria.En}
        >
            <div className={`${snippetInner} space-y-2.5`}>
                <div className="border-b border-grey-100/90 pb-2">
                    <p className="text-left text-[10px] font-semibold text-grey-800">
                        {labels.header[lang] ?? labels.header.En}
                    </p>
                    <div className="mt-1.5 flex gap-1">
                        {tabs.map((tab, index) => (
                            <span
                                key={tab}
                                className={`rounded-md px-1.5 py-0.5 text-[8px] font-semibold ${
                                    index === 0
                                        ? "bg-sky-100 text-sky-700"
                                        : "bg-grey-50 text-grey-500"
                                }`}
                            >
                                {tab}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="rounded-md border border-sky-100 bg-sky-50/60 px-2 py-1.5 text-left">
                    <p className="text-[8px] font-semibold uppercase tracking-wide text-sky-600">
                        Q
                    </p>
                    <p className="mt-0.5 text-[9px] leading-snug text-grey-700">
                        {labels.question[lang] ?? labels.question.En}
                    </p>
                </div>

                <div className={chatThreadClass}>
                    <div
                        className={`${chatBubbleLeftClass} bg-sky-50 ring-1 ring-sky-100`}
                    >
                        <p className={`${chatLabelClass} text-sky-600`}>Sensei</p>
                        <p className={chatBodyClass}>
                            {labels.senseiOpener[lang] ?? labels.senseiOpener.En}
                        </p>
                    </div>
                    <div
                        className={`${chatBubbleRightClass} bg-white shadow-sm ring-1 ring-grey-100`}
                    >
                        <p className={`${chatLabelClass} text-grey-500`}>
                            {lang === "Zh" ? "學生" : lang === "Jp" ? "生徒" : "Student"}
                        </p>
                        <p className={chatBodyClass}>
                            {labels.student[lang] ?? labels.student.En}
                        </p>
                    </div>
                    <div
                        className={`${chatBubbleLeftClass} bg-sky-50 ring-1 ring-sky-100`}
                    >
                        <p className={`${chatLabelClass} text-sky-600`}>Sensei</p>
                        <p className={chatBodyClass}>
                            {labels.sensei[lang] ?? labels.sensei.En}
                        </p>
                    </div>
                </div>

                <div className="flex items-center justify-between gap-2 border-t border-grey-100/90 pt-2">
                    <div className="flex items-center gap-1">
                        <span className="text-[8px] text-grey-500">
                            {labels.step[lang] ?? labels.step.En}
                        </span>
                        <div className="flex gap-0.5">
                            <span className="h-1 w-2 rounded-full bg-sky-500" />
                            <span className="h-1 w-2 rounded-full bg-sky-500" />
                            <span className="h-1 w-2 rounded-full bg-grey-200" />
                            <span className="h-1 w-2 rounded-full bg-grey-200" />
                        </div>
                    </div>
                    <span className="rounded-full bg-grey-50 px-1.5 py-0.5 text-[8px] font-medium text-grey-600">
                        {labels.review[lang] ?? labels.review.En}
                    </span>
                </div>
            </div>
        </div>
    );
}

export function MedicalSenseiCardPreview({ lang }: PreviewProps) {
    const labels = {
        aria: {
            En: "Medical Sensei virtual patient interview preview",
            Zh: "醫學 Sensei 虛擬病人問診預覽",
            Jp: "医学 Sensei バーチャル患者問診プレビュー",
        },
        header: {
            En: "Virtual patient · Interview",
            Zh: "虛擬病人 · 問診練習",
            Jp: "バーチャル患者 · 問診練習",
        },
        patientLabel: {
            En: "Patient",
            Zh: "虛擬病人",
            Jp: "患者",
        },
        learnerLabel: {
            En: "You",
            Zh: "你",
            Jp: "あなた",
        },
        patient1: {
            En: "I've had chest tightness since this morning…",
            Zh: "從今天早上開始胸口有點悶…",
            Jp: "今朝から胸の違和感があります…",
        },
        learner1: {
            En: "Does the discomfort radiate anywhere?",
            Zh: "這種悶痛會放射到其他地方嗎？",
            Jp: "その違和感は他の場所に広がりますか？",
        },
        patient2: {
            En: "It seems to go to my left shoulder.",
            Zh: "好像會到左肩…",
            Jp: "左肩に広がる感じです…",
        },
        history: { En: "History", Zh: "問診", Jp: "問診" },
        reasoning: { En: "Reasoning", Zh: "推理", Jp: "推論" },
        feedback: {
            En: "Prompt: follow up onset and aggravating factors.",
            Zh: "提示：可再追問發作時間與加重因素。",
            Jp: "ヒント：発症時期と増悪因子を追加で確認。",
        },
    } as const;

    const t = (key: keyof typeof labels) =>
        labels[key][lang] ?? labels[key].En;

    return (
        <div
            className={`${snippetShell} min-w-[12.75rem]`}
            role="img"
            aria-label={t("aria")}
        >
            <div className={`${snippetInner} space-y-2.5`}>
                <div className="flex items-center justify-between gap-2 border-b border-grey-100/90 pb-2">
                    <p className="text-left text-[10px] font-semibold text-grey-800">
                        {t("header")}
                    </p>
                    <span className="shrink-0 rounded-full bg-teal-100 px-1.5 py-0.5 text-[8px] font-semibold text-teal-700">
                        Live
                    </span>
                </div>

                <div className={chatThreadClass}>
                    <div className={`${chatBubbleLeftClass} bg-teal-50/80 ring-1 ring-teal-100/80`}>
                        <p className={`${chatLabelClass} text-teal-700`}>
                            {t("patientLabel")}
                        </p>
                        <p className={chatBodyClass}>{t("patient1")}</p>
                    </div>
                    <div className={`${chatBubbleRightClass} bg-white shadow-sm ring-1 ring-grey-100`}>
                        <p className={`${chatLabelClass} text-teal-700`}>
                            {t("learnerLabel")}
                        </p>
                        <p className={chatBodyClass}>{t("learner1")}</p>
                    </div>
                    <div className={`${chatBubbleLeftClass} bg-teal-50/80 ring-1 ring-teal-100/80`}>
                        <p className={`${chatLabelClass} text-teal-700`}>
                            {t("patientLabel")}
                        </p>
                        <p className={chatBodyClass}>{t("patient2")}</p>
                    </div>
                </div>

                <div className="space-y-1 border-t border-grey-100/90 pt-2">
                    <div className="flex items-center gap-2">
                        <span className="w-10 shrink-0 text-[8px] text-grey-500">
                            {t("history")}
                        </span>
                        <div className="flex flex-1 gap-0.5">
                            <span className="h-1 flex-1 rounded-full bg-teal-500" />
                            <span className="h-1 flex-1 rounded-full bg-teal-500" />
                            <span className="h-1 flex-1 rounded-full bg-grey-200" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-10 shrink-0 text-[8px] text-grey-500">
                            {t("reasoning")}
                        </span>
                        <div className="flex flex-1 gap-0.5">
                            <span className="h-1 flex-1 rounded-full bg-teal-400" />
                            <span className="h-1 flex-1 rounded-full bg-grey-200" />
                            <span className="h-1 flex-1 rounded-full bg-grey-200" />
                        </div>
                    </div>
                    <p className="text-left text-[8px] leading-snug text-grey-500">
                        {t("feedback")}
                    </p>
                </div>
            </div>
        </div>
    );
}

export function EducationProductCardPreview({ lang }: PreviewProps) {
    return (
        <div
            className={`${snippetShell} min-w-[11.5rem]`}
            role="img"
            aria-label={
                EDUCATION_LOOP_LABELS.previewAria[lang] ??
                EDUCATION_LOOP_LABELS.previewAria.En
            }
        >
            <div className={snippetInner}>
                <p className="border-b border-grey-100/90 pb-2 text-left text-[11px] font-semibold text-grey-800">
                    {EDUCATION_LOOP_LABELS.header[lang] ??
                        EDUCATION_LOOP_LABELS.header.En}
                </p>
                <p className="mt-2 text-left text-[10px] leading-relaxed text-grey-600">
                    {EDUCATION_LOOP_LABELS.steps[lang] ??
                        EDUCATION_LOOP_LABELS.steps.En}
                </p>
            </div>
        </div>
    );
}

export function ProductSuiteCardPreview({ lang }: PreviewProps) {
    return (
        <div
            className={`${snippetShell} min-w-[11.5rem]`}
            role="img"
            aria-label={t("suitePreviewAria", lang)}
        >
            <div className={snippetInner}>
                <p className="border-b border-grey-100/90 pb-2 text-left text-[11px] font-semibold text-grey-800">
                    {t("suiteHeader", lang)}
                </p>
                <ul className="mt-2 space-y-2 text-left">
                    {SUITE_ROWS.map((row) => {
                        const Icon = row.icon;
                        return (
                            <li
                                key={row.titleKey}
                                className="flex items-start gap-2 text-left"
                            >
                                <span
                                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${row.iconClass}`}
                                    aria-hidden="true"
                                >
                                    <Icon className="h-3 w-3" strokeWidth={1.75} />
                                </span>
                                <div className="min-w-0 text-left">
                                    <p
                                        className={`text-left text-[10px] font-semibold leading-snug ${
                                            "muted" in row && row.muted
                                                ? "text-grey-500"
                                                : "text-grey-800"
                                        }`}
                                    >
                                        {t(row.titleKey, lang)}
                                    </p>
                                    <p className="text-left text-[9px] text-grey-400">
                                        {t(row.subtitleKey, lang)}
                                    </p>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}

export const INTEGRATION_CARD_PREVIEWS: Record<string, ComponentType<PreviewProps>> = {
    doctor: DoctorHandoffCardPreview,
    nurse: NurseHandoffCardPreview,
    patient: PatientIntakeCardPreview,
    hub: ProductSuiteCardPreview,
    education: EducationProductCardPreview,
    "medical-sensei": MedicalSenseiCardPreview,
    "business-sensei": BusinessSenseiCardPreview,
    "k12-sensei": K12SenseiCardPreview,
};

/** @deprecated Use INTEGRATION_CARD_PREVIEWS[cardId] */
export const NURSE_INTEGRATION_CARD_PREVIEWS = [
    DoctorHandoffCardPreview,
    PatientIntakeCardPreview,
    ProductSuiteCardPreview,
] as const;

export function getIntegrationCardPreview(
    cardId: string
): ComponentType<PreviewProps> | undefined {
    return INTEGRATION_CARD_PREVIEWS[cardId];
}
